import React, { useState } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import { migrationScript } from '../../utils/migrationScript';
import toast from 'react-hot-toast';
import { FaDatabase, FaExclamationTriangle, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

const MigrationPage = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleRunMigration = async () => {
    setIsRunning(true);
    setMigrationResult(null);

    try {
      const result = await migrationScript.runFullMigration();
      setMigrationResult(result);

      if (result.success) {
        toast.success('Migration completed successfully!');
      } else {
        toast.error('Migration failed. Check the logs below.');
      }
    } catch (error) {
      toast.error('Migration error: ' + error.message);
      setMigrationResult({
        success: false,
        error: error.message
      });
    } finally {
      setIsRunning(false);
      setShowConfirm(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Database Migration</h1>
          <p className="text-gray-600">Migrate database to multi-language format</p>
        </div>

        {/* Warning Card */}
        <Card className="border-orange-300 bg-orange-50">
          <div className="flex items-start gap-3">
            <FaExclamationTriangle className="text-orange-600 text-2xl mt-1" />
            <div>
              <h3 className="text-lg font-semibold text-orange-900 mb-2">
                Important: Read Before Running Migration
              </h3>
              <ul className="text-sm text-orange-800 space-y-1">
                <li>• This migration converts your database from single-language to multi-language format</li>
                <li>• Existing content will be copied to all 4 languages (nl, fr, de, en)</li>
                <li>• A backup will be created automatically before migration</li>
                <li>• The process may take a few minutes depending on data size</li>
                <li>• Do NOT close this page while migration is running</li>
                <li>• Run this migration only once</li>
              </ul>
            </div>
          </div>
        </Card>

        {/* Migration Status */}
        {migrationResult && (
          <Card className={migrationResult.success ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}>
            <div className="flex items-start gap-3">
              {migrationResult.success ? (
                <FaCheckCircle className="text-green-600 text-2xl mt-1" />
              ) : (
                <FaTimesCircle className="text-red-600 text-2xl mt-1" />
              )}
              <div className="flex-1">
                <h3 className={`text-lg font-semibold mb-2 ${migrationResult.success ? 'text-green-900' : 'text-red-900'}`}>
                  {migrationResult.success ? 'Migration Successful!' : 'Migration Failed'}
                </h3>

                {migrationResult.success && migrationResult.results && (
                  <div className="text-sm text-green-800">
                    <p className="font-medium mb-2">Successfully migrated:</p>
                    <ul className="space-y-1">
                      {migrationResult.results.success.map((item) => (
                        <li key={item}>✓ {item}</li>
                      ))}
                    </ul>
                    {migrationResult.results.backup && (
                      <p className="mt-3">
                        <strong>Backup ID:</strong> {migrationResult.results.backup.timestamp}
                      </p>
                    )}
                  </div>
                )}

                {!migrationResult.success && (
                  <div className="text-sm text-red-800">
                    <p className="font-medium">Error:</p>
                    <p className="mt-1">{migrationResult.error || 'Unknown error occurred'}</p>
                    {migrationResult.results?.errors && migrationResult.results.errors.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {migrationResult.results.errors.map((err, idx) => (
                          <li key={idx}>• {err}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Migration Actions */}
        <Card>
          <h2 className="text-xl font-semibold mb-4">Run Migration</h2>

          {!showConfirm ? (
            <Button
              variant="primary"
              onClick={() => setShowConfirm(true)}
              disabled={isRunning}
            >
              <FaDatabase className="mr-2" />
              Start Database Migration
            </Button>
          ) : (
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-yellow-900 font-medium mb-2">
                  Are you absolutely sure you want to run the migration?
                </p>
                <p className="text-sm text-yellow-800">
                  This will modify your database structure. A backup will be created, but please ensure you understand the changes.
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="danger"
                  onClick={handleRunMigration}
                  loading={isRunning}
                  disabled={isRunning}
                >
                  {isRunning ? 'Migrating...' : 'Yes, Run Migration Now'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setShowConfirm(false)}
                  disabled={isRunning}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* Migration Info */}
        <Card>
          <h2 className="text-xl font-semibold mb-4">What This Migration Does</h2>
          <div className="space-y-3 text-sm text-gray-700">
            <div>
              <h4 className="font-semibold text-gray-900">Company Information:</h4>
              <p>Converts <code className="bg-gray-100 px-1">address</code> to <code className="bg-gray-100 px-1">address_nl</code>, <code className="bg-gray-100 px-1">address_fr</code>, <code className="bg-gray-100 px-1">address_de</code>, <code className="bg-gray-100 px-1">address_en</code></p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900">About Page:</h4>
              <p>Converts company story, mission, and vision to multi-language format</p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900">Team Members:</h4>
              <p>Converts role and bio fields to multi-language format</p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900">Gallery Items:</h4>
              <p>Converts title and description fields to multi-language format</p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900">Categories:</h4>
              <p>Converts category names to multi-language format</p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900">Privacy & Terms:</h4>
              <p>Converts content to multi-language format</p>
            </div>
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default MigrationPage;
